import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  FaClipboardList,
  FaArrowLeft,
  FaKeyboard,
  FaGlobe,
  FaTimes,
  FaGripHorizontal,
} from "react-icons/fa";
import moment from "moment";
import dayjs from "dayjs";
import styles from "../AdmissionApplicationForm.module.css";
import {
  CustomFileInput,
  CustomRadioGroup,
  CustomButton,
  CustomDatePicker,
  CustomTextInput,
  CustomDropdown,
} from "../../../components";
import {
  fetchOccupationOptions,
  fetchCountryOptions,
  fetchStateOptions,
  fetchReligionOptions,
  fetchCategoryOptions,
  fetchClassOptions,
  GENDER_OPTIONS,
  STATE_TYPES,
} from "../../../redux/Masterdata";
import { switchLanguage } from "../../../redux/languageSlice";
import { validateHSCAdmissionForm } from "./ValidationXII";
import AxiosInstance, {
  admissionLogo1,
  admissionLogo2,
} from "../../../services/Api";
import PopupModal from "../../../components/PopupModal/PopupModal";
import ModalXII from "./ModalXII";

// ==================== STATIC OPTIONS ====================
const LIVING_WITH_OPTIONS = [
  { value: "", label: "Select" },
  { value: "parent", label: "Parent" },
  { value: "guardian", label: "Guardian" },
];

const YES_NO_OPTIONS = [
  { value: "Yes", label: "Yes", disabled: false },
  { value: "No", label: "No", disabled: false },
];

const MEDIUM_OPTIONS = [
  { value: "TM", label: "TM", disabled: false },
  { value: "EM", label: "EM", disabled: false },
];

const MOTHER_TONGUE_OPTIONS = [
  { value: "", label: "Select Mother Tongue" },
  { value: "Tamil", label: "Tamil" },
  { value: "English", label: "English" },
  { value: "Hindi", label: "Hindi" },
  { value: "Telugu", label: "Telugu" },
  { value: "Malayalam", label: "Malayalam" },
  { value: "Kannada", label: "Kannada" },
  { value: "Urdu", label: "Urdu" },
  { value: "Others", label: "Others" },
];

const QUALIFYING_EXAM_OPTIONS = [
  { value: "", label: "Select Examination" },
  { value: "SSLC", label: "SSLC" },
  { value: "HSC", label: "HSC" },
  { value: "CBSE 10th", label: "CBSE 10th" },
  { value: "ICSE", label: "ICSE" },
  { value: "Others", label: "Others" },
];

const EXAM_BOARD_OPTIONS = [
  { value: "", label: "Select Examination" },
  { value: "State Board", label: "State Board" },
  { value: "CBSE", label: "CBSE" },
  { value: "ICSE", label: "ICSE" },
  { value: "Matric", label: "Matric" },
  { value: "Others", label: "Others" },
];

const COURSE_PREFERRED_OPTIONS = [
  { value: "", label: "Select Course Preferred" },
  { value: "TM", label: "Tamil" },
  { value: "EM", label: "English" },
];

const YEAR_OF_ADMISSION_OPTIONS = [
  { value: "", label: "Select" },
  { value: "First Year", label: "First Year" },
  { value: "Second Year", label: "Second Year" },
];

// ==================== TAMIL KEYBOARD LAYOUT ====================
const TAMIL_VOWELS = [
  "அ",
  "ஆ",
  "இ",
  "ஈ",
  "உ",
  "ஊ",
  "எ",
  "ஏ",
  "ஐ",
  "ஒ",
  "ஓ",
  "ஔ",
];
const TAMIL_CONSONANTS = [
  "க",
  "ங",
  "ச",
  "ஞ",
  "ட",
  "ண",
  "த",
  "ந",
  "ப",
  "ம",
  "ய",
  "ர",
  "ல",
  "வ",
  "ழ",
  "ள",
  "ற",
  "ன",
  "ஜ",
  "ஷ",
  "ஸ",
  "ஹ",
];
const TAMIL_MODIFIERS = [
  "்",
  "ா",
  "ி",
  "ீ",
  "ு",
  "ூ",
  "ெ",
  "ே",
  "ை",
  "ொ",
  "ோ",
  "ௌ",
];

// ==================== NUMBER-ONLY FIELDS ====================
const NUMBER_ONLY_FIELDS = [
  "mobile_number",
  "aadhar_no",
  "parent_pincode",
  "guardian_pincode",
  "emis_id",
  "parent_annual_salary",
  "guardian_annual_salary",
  "tamil_mark",
  "english_mark",
  "mathematics_mark",
  "science_mark",
  "social_science_mark",
  "register_number",
];

const MARK_FIELDS = [
  "tamil_mark",
  "english_mark",
  "mathematics_mark",
  "science_mark",
  "social_science_mark",
];

// ==================== MARKS CALCULATION ====================
const calculateTotals = (data) => {
  const marks = MARK_FIELDS.map((f) => parseInt(data[f]) || 0);
  const total = marks.reduce((a, b) => a + b, 0);
  const pct = parseFloat(((total / 500) * 100).toFixed(2));
  return { total_mark: total, total_percentage: pct };
};

// ==================== INITIAL FORM DATA ====================
const getInitialFormData = () => ({
  date: moment().format("YYYY-MM-DD"),
  applicant_name_in_tamil: "",
  applicant_name: "",
  date_of_birth: null,
  aadhar_no: "",
  emis_id: "",
  email: "",
  mobile_number: "",
  student_image: "",
  student_image_original_name: "",
  gender: "",
  nationality: "Indian",
  student_native_state: "",
  place_of_birth: "",
  religion: "",
  caste: "",
  community: "",
  community_certificate_path: "",
  community_certificate_original_name: "",
  living_with: "",
  parent_name: "",
  parent_occupation: "",
  parent_annual_salary: "",
  parent_address: "",
  parent_district: "",
  parent_city: "",
  parent_state: "",
  parent_pincode: "",
  guardian_name: "",
  guardian_occupation: "",
  guardian_annual_salary: "",
  guardian_address: "",
  guardian_district: "",
  guardian_city: "",
  guardian_state: "",
  guardian_pincode: "",
  qualified_eligibility_exam: "",
  exam_board_name: "",
  qualified_exam_name: "",
  exam_month_and_year: null,
  register_number: "",
  tamil_mark: "",
  english_mark: "",
  mathematics_mark: "",
  science_mark: "",
  social_science_mark: "",
  total_mark: 0,
  total_percentage: 0,
  preferred_study_course: "",
  admission_for_which_year: "",
  preferred_group_1: "",
  preferred_group_2: "",
  preferred_group_3: "",
  mother_tongue: "",
  last_medium_of_instruction: "",
  last_studied_class: "",
  studied_year: null,
  last_studied_school_name: "",
  reason_for_discontinuing: "",
  willing_to_join_services: "No",
  is_vaccinated_pox: "Yes",
  certificate_path: "",
  certificate_original_name: "",
  place: "",
});

const SUPPORTED_URL_LANGS = ["en", "ta"];

// ==================== MAIN COMPONENT ====================
const AdmissionApplicationFormXII = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { "*": splat } = useParams();
  const { translations, language } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);

  // Parse splat into viewId, urlLang, admissionId based on content
  const { viewId, urlLang, admissionId } = useMemo(() => {
    if (!splat) return { viewId: null, urlLang: null, admissionId: "" };

    const segments = splat.split("/").filter(Boolean);
    let viewId = null;
    let urlLang = null;

    const remaining = [];
    segments.forEach((seg) => {
      if (/^\d+$/.test(seg) && viewId === null) {
        viewId = seg;
      } else if (SUPPORTED_URL_LANGS.includes(seg) && urlLang === null) {
        urlLang = seg;
      } else {
        remaining.push(seg);
      }
    });

    const admissionId = remaining.join("/");

    return { viewId, urlLang, admissionId };
  }, [splat]);

  // 🔹 Switch language if URL has lang segment
  useEffect(() => {
    if (
      urlLang &&
      SUPPORTED_URL_LANGS.includes(urlLang) &&
      urlLang !== language
    ) {
      dispatch(switchLanguage(urlLang));
      localStorage.setItem("appLanguage", urlLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlLang]);

  // ==================== REDUX STATE ====================
  const {
    occupationOptions: reduxOccupationOptions,
    religionOptions: reduxReligionOptions,
    categoryOptions: reduxCategoryOptions,
    fatherStateOptions: reduxFatherStateOptions,
    classOptions: reduxClassOptions,
  } = useSelector((state) => state.masterData);

  // ==================== LOCAL STATE ====================
  const [formData, setFormData] = useState(getInitialFormData());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [admissionConfig, setAdmissionConfig] = useState(null);

  // School logo — fetched live through the public blob-aware endpoint
  // (plain <img src> cannot send the Tenant-ID header, so fetch as a
  // blob via AxiosInstance and show an object URL). Falls back to the
  // bundled default logos until it loads / on failure.
  const [schoolLogoUrl, setSchoolLogoUrl] = useState(null);
  useEffect(() => {
    const fetchSchoolLogo = async () => {
      try {
        const res = await AxiosInstance.get("/admission/school-logo-image", {
          params: { school_id: admissionConfig.school_id },
          responseType: "blob",
        });
        setSchoolLogoUrl(URL.createObjectURL(res.data));
      } catch (err) {
        // keep the default bundled logo
      }
    };
    if (admissionConfig?.school_id) fetchSchoolLogo();
  }, [admissionConfig?.school_id]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [groupOptions, setGroupOptions] = useState([]);
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
    redirectPath: "",
  });
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [admissionStatus, setAdmissionStatus] = useState(null);
  const [pendingViewData, setPendingViewData] = useState(null);

  // ---- Tamil Keyboard State ----
  const [showTamilKeyboard, setShowTamilKeyboard] = useState(false);
  const [activeInputName, setActiveInputName] = useState("");
  const [kbPosition, setKbPosition] = useState({ x: 100, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const keyboardRef = useRef(null);
  const otpInputRef = useRef(null);

  const appLanguageOptions = [
    { code: "en", label: "English" },
    { code: "ta", label: "தமிழ்" },
  ];

  const handleLanguageChange = (e) => {
    const langCode = e.target.value;
    dispatch(switchLanguage(langCode));
    localStorage.setItem("appLanguage", langCode);
  };

  // ⭐ Edit mode detection
  const isEditMode = useMemo(() => Boolean(viewId), [viewId]);

  // ==================== DRAGGABLE KEYBOARD HANDLERS ====================
  const handleDragStart = useCallback(
    (e) => {
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      dragOffset.current = {
        x: clientX - kbPosition.x,
        y: clientY - kbPosition.y,
      };
      setIsDragging(true);
    },
    [kbPosition],
  );

  const handleDragMove = useCallback(
    (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const newX = clientX - dragOffset.current.x;
      const newY = clientY - dragOffset.current.y;
      setKbPosition({
        x: Math.max(0, Math.min(newX, window.innerWidth - 50)),
        y: Math.max(0, Math.min(newY, window.innerHeight - 50)),
      });
    },
    [isDragging],
  );

  const handleDragEnd = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleDragMove);
      window.addEventListener("mouseup", handleDragEnd);
      window.addEventListener("touchmove", handleDragMove, { passive: false });
      window.addEventListener("touchend", handleDragEnd);
    }
    return () => {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("touchmove", handleDragMove);
      window.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // ==================== MEMOIZED OPTIONS ====================
  const genderOptions = useMemo(
    () =>
      GENDER_OPTIONS.map((opt) => ({
        ...opt,
        label: t?.[opt.label?.toLowerCase()] || opt.label,
      })),
    [t],
  );

  const religionOptions = useMemo(
    () => [
      { value: "", label: t?.selectReligion || "Select Religion" },
      ...reduxReligionOptions,
    ],
    [reduxReligionOptions, t],
  );

  const categoryOptions = useMemo(
    () => [
      { value: "", label: t?.selectCategory || "Select Category" },
      ...reduxCategoryOptions,
    ],
    [reduxCategoryOptions, t],
  );

  const stateOptions = useMemo(
    () => [
      { value: "", label: t?.selectState || "Select State" },
      ...reduxFatherStateOptions,
    ],
    [reduxFatherStateOptions, t],
  );

  const occupationOptions = useMemo(
    () => [
      { value: "", label: t?.selectOccupation || "Select Occupation" },
      ...reduxOccupationOptions,
    ],
    [reduxOccupationOptions, t],
  );

  const classOptions = useMemo(() => {
    const filteredClasses = reduxClassOptions.filter(
      (item) => item.label === "X" || item.label === "XI",
    );
    return [
      { value: "", label: t?.selectClass || "Select Class" },
      ...filteredClasses,
    ];
  }, [reduxClassOptions, t]);

  const livingWithOptions = useMemo(
    () =>
      LIVING_WITH_OPTIONS.map((opt) => ({
        ...opt,
        label:
          opt.value === ""
            ? t?.select || "Select"
            : opt.value === "parent"
              ? t?.parent || "Parent"
              : t?.guardian || "Guardian",
      })),
    [t],
  );

  const motherTongueOptions = useMemo(
    () =>
      MOTHER_TONGUE_OPTIONS.map((opt) => ({
        ...opt,
        label:
          opt.value === ""
            ? t?.selectMotherTongue || "Select Mother Tongue"
            : opt.label,
      })),
    [t],
  );

  const preferredGroupOptions = useMemo(
    () => [
      { value: "", label: t?.select || "Select" },
      ...groupOptions.map((g) => ({
        value: g.group_name || g.value,
        label: g.name || g.label,
        medium: g.medium,
      })),
    ],
    [groupOptions, t],
  );

  // ==================== INITIAL DATA FETCH ====================
  useEffect(() => {
    if (viewId) {
      fetchAdmissionView();
    } else {
      fetchAdmissionCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch groups when year selection changes
  useEffect(() => {
    if (formData.admission_for_which_year) {
      fetchHSGroupList(formData.admission_for_which_year);
    } else {
      setGroupOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.admission_for_which_year]);

  const fetchAdmissionCheck = async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.post("/admission/admissionCheck", {
        id: admissionId,
        admission_type: "XI-XII",
      });
      if (response.data?.status) {
        setAdmissionConfig(response.data.data);
        fetchAllMasterData(response.data.data);
      } else {
        setModalState({
          open: true,
          type: "failed",
          message:
            response.data?.message ||
            "Failed to fetch details. Please try again.",
        });
      }
    } catch (error) {
      console.error("Admission check error:", error);
      setModalState({
        open: true,
        type: "error",
        message: t?.fetchFailed || "Failed to fetch details. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch existing HS admission for editing
  const fetchAdmissionView = async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.post(
        "/admission/viewHigherSecondryAdmission",
        { id: viewId },
      );
      if (response.data?.status) {
        const data = response.data.data;
        setAdmissionStatus(data.status);
        const formLang = data?.form_language || "en";
        dispatch(switchLanguage(formLang));
        setAdmissionConfig({
          school_id: data.school_id,
          academic_year_id: data.academic_years,
          school_name: data.school_name || "",
        });

        fetchAllMasterData({
          school_id: data.school_id,
          academic_year_id: data.academic_year,
        });

        // ⭐ If status is Withdraw → show popup and block editing
        if (data.status === "Withdraw") {
          setModalState({
            open: true,
            type: "failed",
            message:
              t?.statusWithdrawMessage ||
              "Status is Withdraw. You cannot edit this application.",
            redirectPath: "/",
          });
          return;
        }

        setPendingViewData(data);
        populateFormData(data);
      } else {
        setModalState({
          open: true,
          type: "failed",
          message:
            response.data?.message ||
            "Failed to fetch admission details. Please try again.",
        });
      }
    } catch (error) {
      console.error("Admission view error:", error);
      setModalState({
        open: true,
        type: "error",
        message: t?.fetchFailed || "Failed to fetch details. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Map API response → formData
  const populateFormData = (data) => {
    const studiedYear = data.studied_year
      ? dayjs(`${data.studied_year}-01-01`).format("YYYY-MM")
      : null;

    setFormData({
      date: data.date
        ? dayjs(data.date).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD"),
      applicant_name_in_tamil: data.applicant_name_in_tamil || "",
      applicant_name: data.applicant_name || "",
      date_of_birth: data.date_of_birth
        ? dayjs(data.date_of_birth).format("YYYY-MM-DD")
        : null,
      aadhar_no: data.aadhaar_no || "",
      emis_id: data.emis_id || "",
      email: data.email || "",
      mobile_number: data.mobile_number || "",
      student_image: data.student_image || "",
      student_image_original_name: data.student_original_path || "",
      gender: data.gender || "",
      nationality: data.nationality || "Indian",
      student_native_state: data.student_native_state || "",
      place_of_birth: data.place_of_birth || "",
      religion: data.religion || "",
      caste: data.caste || "",
      community: data.community || "",
      community_certificate_path: data.community_certificate_path || "",
      community_certificate_original_name: data.community_original_path || "",
      living_with: data.living_with || "",
      parent_name: data.parent_name || "",
      parent_occupation: data.parent_occupation || "",
      parent_annual_salary: data.parent_annual_salary
        ? String(data.parent_annual_salary)
        : "",
      parent_address: data.parent_address || "",
      parent_district: data.parent_district || "",
      parent_city: data.parent_city || "",
      parent_state: data.parent_state || "",
      parent_pincode: data.parent_pincode ? String(data.parent_pincode) : "",
      guardian_name: data.guardian_name || "",
      guardian_occupation: data.guardian_occupation || "",
      guardian_annual_salary: data.guardian_annual_salary
        ? String(data.guardian_annual_salary)
        : "",
      guardian_address: data.guardian_address || "",
      guardian_district: data.guardian_district || "",
      guardian_city: data.guardian_city || "",
      guardian_state: data.guardian_state || "",
      guardian_pincode: data.guardian_pincode
        ? String(data.guardian_pincode)
        : "",
      qualified_eligibility_exam: data.qualified_eligibility_exam || "",
      exam_board_name: data.exam_board_name || "",
      qualified_exam_name: data.qualified_exam_name || "",
      exam_month_and_year: data.exam_month_and_year || null,
      register_number: data.register_number || "",
      tamil_mark: data.tamil_mark != null ? String(data.tamil_mark) : "",
      english_mark: data.english_mark != null ? String(data.english_mark) : "",
      mathematics_mark:
        data.mathematics_mark != null ? String(data.mathematics_mark) : "",
      science_mark: data.science_mark != null ? String(data.science_mark) : "",
      social_science_mark:
        data.social_science_mark != null
          ? String(data.social_science_mark)
          : "",
      total_mark: data.total_mark || 0,
      total_percentage: parseFloat(data.total_percentage) || 0,
      preferred_study_course: data.preferred_study_course || "",
      admission_for_which_year: data.admission_for_which_year || "",
      preferred_group_1: data.preferred_group_1 || "",
      preferred_group_2: data.preferred_group_2 || "",
      preferred_group_3: data.preferred_group_3 || "",
      mother_tongue: data.mother_tongue || "",
      last_medium_of_instruction: data.last_medium_of_instruction || "",
      last_studied_class: data.last_studied_class || "",
      studied_year: studiedYear,
      last_studied_school_name: data.last_studied_school_name || "",
      reason_for_discontinuing: data.reason_for_discontinuing || "",
      willing_to_join_services: data.willing_to_join_services || "No",
      is_vaccinated_pox: data.is_vaccinated_pox || "Yes",
      certificate_path: data.certificate_path || "",
      certificate_original_name: data.certificate_original_path || "",
      place: data.place || "",
    });
  };

  const fetchHSGroupList = async (className) => {
    try {
      if (!className) return;
      if (!admissionConfig) return;
      const yearValue =
        typeof className === "object" ? className.value : className;
      const classValue = yearValue === "First Year" ? "XI" : "XII";

      const response = await AxiosInstance.post("/admission/hsgroupssections", {
        academic_id: admissionConfig.academic_year_id,
        school_id: admissionConfig.school_id,
        class: classValue,
      });

      if (response.data?.status && response.data?.data) {
        const groups = response.data.data;
        const options = groups.map((group) => ({
          value: group.group_name,
          label: group.name,
          medium: group.medium,
          group_name: group.group_name,
        }));
        setGroupOptions(options);

        if (pendingViewData) {
          setFormData((prev) => ({
            ...prev,
            preferred_group_1: pendingViewData.preferred_group_1 || "",
            preferred_group_2: pendingViewData.preferred_group_2 || "",
            preferred_group_3: pendingViewData.preferred_group_3 || "",
          }));
          setPendingViewData(null);
        }
      }
    } catch (error) {
      console.error("HS Group fetch error:", error);
    }
  };

  const fetchAllMasterData = (data) => {
    dispatch(fetchReligionOptions());
    dispatch(fetchCategoryOptions());
    dispatch(fetchOccupationOptions());
    dispatch(
      fetchClassOptions({
        id: data.school_id,
        academic_year: data.academic_year_id,
      }),
    );
    dispatch(fetchCountryOptions()).then((action) => {
      if (action.payload && Array.isArray(action.payload)) {
        const india = action.payload.find(
          (c) => c.label?.toLowerCase() === "india",
        );
        if (india) {
          dispatch(
            fetchStateOptions({
              countryId: india.value,
              stateType: STATE_TYPES.FATHER,
            }),
          );
        }
      }
    });
  };

  // ==================== HANDLERS ====================
  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      if (NUMBER_ONLY_FIELDS.includes(name)) {
        if (value !== "" && !/^\d*$/.test(value)) return;
      }
      if (MARK_FIELDS.includes(name)) {
        const numVal = parseInt(value);
        if (value !== "" && (isNaN(numVal) || numVal < 0 || numVal > 100))
          return;
        setFormData((prev) => {
          const updated = { ...prev, [name]: value };
          const { total_mark, total_percentage } = calculateTotals(updated);
          return { ...updated, total_mark, total_percentage };
        });
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
        return;
      }
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  const handleDropdownChange = useCallback(
    (name, value) => {
      if (name.startsWith("preferred_group_")) {
        if (typeof value === "object" && value !== null) {
          setFormData((prev) => ({ ...prev, [name]: value.value }));
        } else {
          setFormData((prev) => ({ ...prev, [name]: value }));
        }
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
      if (name === "admission_for_which_year") {
        setFormData((prev) => ({
          ...prev,
          preferred_group_1: "",
          preferred_group_2: "",
          preferred_group_3: "",
        }));
      }
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  const handleRadioChange = useCallback(
    (name, value) => {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  const handleDateChange = useCallback(
    (name, date) => {
      const formatted = date ? dayjs(date).format("YYYY-MM-DD") : null;
      setFormData((prev) => ({ ...prev, [name]: formatted }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  const handleMonthYearChange = useCallback(
    (name, date) => {
      const formatted = date ? dayjs(date).format("YYYY-MM") : null;
      setFormData((prev) => ({ ...prev, [name]: formatted }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  // ---- Tamil Keyboard ----
  const handleTamilInputFocus = (name) => {
    if (!NUMBER_ONLY_FIELDS.includes(name)) setActiveInputName(name);
  };
  const handleTamilKeyPress = (char) => {
    if (!activeInputName) return;
    setFormData((prev) => ({
      ...prev,
      [activeInputName]: (prev[activeInputName] || "") + char,
    }));
    if (errors[activeInputName])
      setErrors((prev) => ({ ...prev, [activeInputName]: "" }));
  };
  const handleTamilBackspace = () => {
    if (!activeInputName) return;
    setFormData((prev) => ({
      ...prev,
      [activeInputName]: (prev[activeInputName] || "").slice(0, -1),
    }));
  };
  const handleTamilSpace = () => {
    if (!activeInputName) return;
    setFormData((prev) => ({
      ...prev,
      [activeInputName]: (prev[activeInputName] || "") + " ",
    }));
  };

  // ---- File Upload ----
  const handleFileUpload = useCallback(
    async (file, uploadKey, pathField, originalNameField, allowedTypes) => {
      if (!file) {
        setFormData((prev) => ({
          ...prev,
          [pathField]: "",
          [originalNameField]: "",
        }));
        setErrors((prev) => ({ ...prev, [pathField]: "" }));
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [pathField]: t?.allowedPdfJpgPng || "Allowed: PDF/JPG/PNG",
        }));
        return;
      }
      try {
        const payload = new FormData();
        payload.append(uploadKey, file);
        if (admissionConfig?.school_id) {
          payload.append("school_id", admissionConfig.school_id);
        }
        const response = await AxiosInstance.post(
          "/groupCockpit/upload",
          payload,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        if (Array.isArray(response.data) && response.data.length > 0) {
          const uploaded = response.data[0];
          setFormData((prev) => ({
            ...prev,
            [originalNameField]: uploaded.original_name,
            [pathField]: uploaded.upload_path,
          }));
          setErrors((prev) => ({ ...prev, [pathField]: "" }));
        }
      } catch (error) {
        console.error("Upload error:", error);
        setErrors((prev) => ({
          ...prev,
          [pathField]: t?.fileUploadFailed || "Failed to upload file.",
        }));
      }
    },
    [t, admissionConfig?.school_id],
  );

  const handleStudentImageChange = useCallback(
    (file) =>
      handleFileUpload(
        file,
        "admission_student_image",
        "student_image",
        "student_image_original_name",
        ["image/jpeg", "image/jpg", "image/png"],
      ),
    [handleFileUpload],
  );

  const handleCommunityFileChange = useCallback(
    (file) =>
      handleFileUpload(
        file,
        "community_certificate",
        "community_certificate_path",
        "community_certificate_original_name",
        ["application/pdf", "image/jpeg", "image/jpg", "image/png"],
      ),
    [handleFileUpload],
  );

  const handleCoCurricularFileChange = useCallback(
    (file) =>
      handleFileUpload(
        file,
        "co-curricular_certificate",
        "certificate_path",
        "certificate_original_name",
        ["application/pdf", "image/jpeg", "image/jpg", "image/png"],
      ),
    [handleFileUpload],
  );

  // ==================== VERIFY APPLICATION ====================
  const handleVerifyApplication = async (e) => {
    e.preventDefault();
    const validationErrors = validateHSCAdmissionForm(formData, translations);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstErrorKey = Object.keys(validationErrors)[0];
      const el = document.querySelector(`[name="${firstErrorKey}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    try {
      setLoading(true);
      const otp = Math.floor(100000 + Math.random() * 900000).toString();
      const response = await AxiosInstance.post("/admission/sendOTP", {
        school_id: admissionId,
        email: formData.email,
        otp: otp,
      });
      if (response.data?.status) {
        setGeneratedOtp(otp);
        setModalState({
          open: true,
          type: "success",
          message: t?.otpSentSuccessfully || "OTP sent successfully.",
          redirectPath: "",
        });
        setErrors({});
        setShowPreviewModal(true);
      }
    } catch (error) {
      console.error("Admission check error:", error);
      setModalState({
        open: true,
        type: "error",
        message: t?.fetchFailed || "Failed to fetch details. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ==================== CONFIRM & SUBMIT ====================
  const handleConfirmSubmit = async (otp) => {
    const getVal = (field) => {
      if (!field) return null;
      if (typeof field === "object" && field.value !== undefined)
        return field.value;
      return field;
    };
    const getLabel = (field) => {
      if (!field) return null;
      if (typeof field === "object" && field.label !== undefined)
        return field.label;
      return field;
    };

    const livingWith = getVal(formData.living_with);
    const isParent = livingWith === "parent";
    const isGuardian = livingWith === "guardian";
    const year = formData.studied_year?.split("-")[0];

    try {
      setSubmitting(true);

      // ⭐ Build base payload (common fields for both create and update)
      const basePayload = {
        aadhaar_no: formData.aadhar_no,
        date: formData.date,
        applicant_name: formData.applicant_name,
        applicant_name_in_tamil: formData.applicant_name_in_tamil,
        emis_id: formData.emis_id || null,
        email: formData.email,
        mobile_number: formData.mobile_number,
        student_image: formData.student_image || null,
        student_original_path: formData.student_image_original_name || null,
        date_of_birth: formData.date_of_birth,
        gender: getLabel(formData.gender),
        nationality: formData.nationality,
        student_native_state: getLabel(formData.student_native_state),
        place_of_birth: formData.place_of_birth,
        religion: getLabel(formData.religion),
        caste: formData.caste,
        community: getLabel(formData.community),
        community_certificate_path: formData.community_certificate_path || null,
        community_original_path:
          formData.community_certificate_original_name || null,
        living_with: livingWith,
        parent_name: isParent ? formData.parent_name : null,
        parent_occupation: isParent
          ? getLabel(formData.parent_occupation)
          : null,
        parent_annual_salary: isParent
          ? parseInt(formData.parent_annual_salary) || 0
          : null,
        parent_address: isParent ? formData.parent_address : null,
        parent_city: isParent ? formData.parent_city : null,
        parent_district: isParent ? formData.parent_district : null,
        parent_state: isParent ? getLabel(formData.parent_state) : null,
        parent_pincode: isParent ? formData.parent_pincode : null,
        guardian_name: isGuardian ? formData.guardian_name : null,
        guardian_occupation: isGuardian
          ? getLabel(formData.guardian_occupation)
          : null,
        guardian_annual_salary: isGuardian
          ? parseInt(formData.guardian_annual_salary) || 0
          : null,
        guardian_address: isGuardian ? formData.guardian_address : null,
        guardian_city: isGuardian ? formData.guardian_city : null,
        guardian_district: isGuardian ? formData.guardian_district : null,
        guardian_state: isGuardian ? getLabel(formData.guardian_state) : null,
        guardian_pincode: isGuardian ? formData.guardian_pincode : null,
        qualified_eligibility_exam: getVal(formData.qualified_eligibility_exam),
        exam_board_name: getVal(formData.exam_board_name),
        qualified_exam_name: getVal(formData.qualified_exam_name),
        exam_month_and_year: formData.exam_month_and_year,
        register_number: formData.register_number,
        tamil_mark: parseInt(formData.tamil_mark) || 0,
        english_mark: parseInt(formData.english_mark) || 0,
        mathematics_mark: parseInt(formData.mathematics_mark) || 0,
        science_mark: parseInt(formData.science_mark) || 0,
        social_science_mark: parseInt(formData.social_science_mark) || 0,
        total_mark: formData.total_mark,
        total_percentage: formData.total_percentage,
        preferred_study_course: getVal(formData.preferred_study_course),
        admission_for_which_year: getVal(formData.admission_for_which_year),
        preferred_group_1: formData.preferred_group_1 || null,
        preferred_group_2: formData.preferred_group_2 || null,
        preferred_group_3: formData.preferred_group_3 || null,
        preferred_group_1_medium: formData.preferred_group_1
          ? groupOptions.find((g) => g.value === formData.preferred_group_1)
              ?.medium || null
          : null,
        preferred_group_2_medium: formData.preferred_group_2
          ? groupOptions.find((g) => g.value === formData.preferred_group_2)
              ?.medium || null
          : null,
        preferred_group_3_medium: formData.preferred_group_3
          ? groupOptions.find((g) => g.value === formData.preferred_group_3)
              ?.medium || null
          : null,
        mother_tongue: getVal(formData.mother_tongue),
        last_medium_of_instruction: formData.last_medium_of_instruction,
        last_studied_class: getLabel(formData.last_studied_class),
        studied_year: year,
        last_studied_school_name: formData.last_studied_school_name,
        reason_for_discontinuing: formData.reason_for_discontinuing,
        willing_to_join_services: formData.willing_to_join_services,
        is_vaccinated_pox: formData.is_vaccinated_pox,
        certificate_path: formData.certificate_path || null,
        certificate_original_path: formData.certificate_original_name || null,
        place: formData.place,
        form_language: language,
      };

      let response;

      if (isEditMode) {
        // ⭐ EDIT MODE: PUT to updateHigherSecondryAdmission
        const updatePayload = {
          ...basePayload,
          id: viewId,
          status: admissionStatus || "Submitted",
          updated_by: "Parent",
        };

        response = await AxiosInstance.put(
          "/admission/updateHigherSecondryAdmission",
          updatePayload,
        );
      } else {
        // ⭐ CREATE MODE: POST to createHigherSecondryAdmission
        const createPayload = {
          ...basePayload,
          school_id: admissionId,
          created_by: "Parent",
        };

        response = await AxiosInstance.post(
          "/admission/createHigherSecondryAdmission",
          createPayload,
        );
      }

      if (response.data?.status) {
        setShowPreviewModal(false);
        setGeneratedOtp("");
        setModalState({
          open: true,
          type: "success",
          message: isEditMode
            ? t?.updateSuccess || "Updated successfully!"
            : t?.createSuccess || "Created successfully!",
          // redirectPath: "/",
        });
        if (!isEditMode) {
          setFormData(getInitialFormData());
        }
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response.data?.message ||
            t?.saveFailed ||
            "Failed to save. Please try again.",
        });
      }
    } catch (error) {
      console.error("Submit error:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error.response?.data?.message ||
          t?.saveFailed ||
          "Failed to save. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessOk = () => {
    const { redirectPath } = modalState;
    const pathToNavigate = redirectPath;
    setModalState({ open: false, type: "", message: "", redirectPath: "" });
    if (pathToNavigate) {
      if (typeof pathToNavigate === "object" && pathToNavigate.path) {
        navigate(pathToNavigate.path, {
          state: pathToNavigate.state,
          replace: pathToNavigate.replace || false,
        });
      } else if (
        typeof pathToNavigate === "string" &&
        pathToNavigate.trim() !== ""
      ) {
        navigate(pathToNavigate);
      }
    }
  };

  const handleModalClose = () =>
    setModalState({ open: false, type: "", message: "" });

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        {t?.loading || "Loading..."}
      </div>
    );
  }

  const getResidentialFieldName = (parentField, guardianField) => {
    const livingWithVal =
      typeof formData.living_with === "object"
        ? formData.living_with?.value
        : formData.living_with;
    return livingWithVal === "guardian" ? guardianField : parentField;
  };

  const getResidentialValue = (parentField, guardianField) => {
    const livingWithVal =
      typeof formData.living_with === "object"
        ? formData.living_with?.value
        : formData.living_with;
    return livingWithVal === "guardian"
      ? formData[guardianField]
      : formData[parentField];
  };

  const getResidentialError = (parentField, guardianField) => {
    const livingWithVal =
      typeof formData.living_with === "object"
        ? formData.living_with?.value
        : formData.living_with;
    return livingWithVal === "guardian"
      ? errors[guardianField]
      : errors[parentField];
  };

  const tamilKeyStyle = {
    minWidth: "38px",
    height: "38px",
    border: "1px solid #d0d0d0",
    borderRadius: "6px",
    backgroundColor: "#fff",
    fontSize: "16px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    transition: "background-color 0.15s",
    padding: "0 4px",
    userSelect: "none",
  };

  // ==================== RENDER ====================
  return (
    <>
      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={
          modalState.type === "success"
            ? "Success"
            : modalState.type || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
        onClose={handleModalClose}
      />

      <ModalXII
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        formData={formData}
        onSubmit={handleConfirmSubmit}
        submitting={submitting}
        handleVerifyApplication={handleVerifyApplication}
        generatedOtp={generatedOtp}
        isEditMode={isEditMode}
        otpInputRef={otpInputRef}
      />

      {/* ==================== DRAGGABLE TAMIL KEYBOARD ==================== */}
      {showTamilKeyboard && (
        <div
          ref={keyboardRef}
          style={{
            position: "fixed",
            top: `${kbPosition.y}px`,
            left: `${kbPosition.x}px`,
            zIndex: 1050,
            backgroundColor: "#f8f8f8",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
            width: "auto",
            maxWidth: "620px",
            minWidth: "380px",
            userSelect: "none",
          }}
        >
          <div
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 16px",
              background: "linear-gradient(135deg, #37474f 0%, #455a64 100%)",
              borderRadius: "12px 12px 0 0",
              cursor: isDragging ? "grabbing" : "grab",
              color: "#fff",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <FaGripHorizontal size={14} style={{ opacity: 0.6 }} />
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: "15px" }}>
                {t?.tamilKeyboard || "தமிழ் விசைப்பலகை"}
              </h5>
            </div>
            <button
              type="button"
              onClick={() => setShowTamilKeyboard(false)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "16px",
                color: "#fff",
              }}
            >
              <FaTimes />
            </button>
          </div>
          <div style={{ padding: "12px 16px 16px" }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "5px",
                marginBottom: "8px",
              }}
            >
              {TAMIL_VOWELS.map((char) => (
                <button
                  key={char}
                  type="button"
                  onClick={() => handleTamilKeyPress(char)}
                  style={tamilKeyStyle}
                >
                  {char}
                </button>
              ))}
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "5px",
                marginBottom: "8px",
              }}
            >
              {TAMIL_CONSONANTS.map((char) => (
                <button
                  key={char}
                  type="button"
                  onClick={() => handleTamilKeyPress(char)}
                  style={tamilKeyStyle}
                >
                  {char}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
              {TAMIL_MODIFIERS.map((char, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleTamilKeyPress(char)}
                  style={tamilKeyStyle}
                >
                  {char}
                </button>
              ))}
              <button
                type="button"
                onClick={handleTamilBackspace}
                style={{
                  ...tamilKeyStyle,
                  minWidth: "44px",
                  backgroundColor: "#ffebee",
                  color: "#d32f2f",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                ⌫
              </button>
              <button
                type="button"
                onClick={handleTamilSpace}
                style={{
                  ...tamilKeyStyle,
                  minWidth: "60px",
                  backgroundColor: "#f5f5f5",
                  fontSize: "11px",
                  color: "#555",
                }}
              >
                ␣
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== SCHOOL HEADER BANNER ==================== */}
      <div
        style={{
          background:
            "linear-gradient(135deg, #1565c0 0%, #1976d2 50%, #1e88e5 100%)",
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: "#fff",
          marginBottom: "0",
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <img
            src={schoolLogoUrl || admissionLogo1}
            alt="School Logo"
            style={{
              width: "70px",
              height: "70px",
              borderRadius: "50%",
              objectFit: "cover",
              border: "3px solid rgba(255,255,255,0.5)",
              backgroundColor: "#fff",
            }}
          />
        </div>
        <div style={{ textAlign: "center", flex: 1, padding: "0 16px" }}>
          <h2
            style={{
              margin: "0 0 4px 0",
              fontSize: "22px",
              fontWeight: 700,
              letterSpacing: "0.5px",
            }}
          >
            {admissionConfig?.school_name || t?.schoolName || "School Name"}
          </h2>
          <p
            style={{
              margin: "0 0 2px 0",
              fontSize: "14px",
              fontWeight: 500,
              opacity: 0.9,
              color: "#fff",
            }}
          >
            {t?.formXItoXII || "Form - XI to XII"}
          </p>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              fontWeight: 500,
              opacity: 0.9,
              color: "#fff",
            }}
          >
            {t?.academicYear || "Academic Year"}:{" "}
            <strong>{admissionConfig?.academic_year_id || ""}</strong>
          </p>
        </div>
        <div style={{ flexShrink: 0 }}>
          <img
            src={schoolLogoUrl || admissionLogo2}
            alt="School Logo"
            style={{
              width: "70px",
              height: "70px",
              borderRadius: "50%",
              objectFit: "cover",
              border: "3px solid rgba(255,255,255,0.5)",
              backgroundColor: "#fff",
            }}
          />
        </div>
      </div>

      <div className={styles.container}>
        {/* ==================== TOP TOOLBAR ==================== */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 0",
            marginBottom: "10px",
            borderBottom: "1px solid #e0e0e0",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                backgroundColor: "#1565c0",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <FaArrowLeft size={14} />
              {t?.back || "Back"}
            </button>
            <button
              type="button"
              onClick={() => setShowTamilKeyboard((prev) => !prev)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                backgroundColor: showTamilKeyboard ? "#2e7d32" : "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <FaKeyboard size={14} />
              {t?.tamilKeyboard || "தமிழ் விசைப்பலகை"}
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FaGlobe size={16} style={{ color: "#555" }} />
            <select
              value={language}
              onChange={handleLanguageChange}
              style={{
                padding: "8px 32px 8px 12px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                fontSize: "14px",
                fontWeight: 500,
                backgroundColor: "#fff",
                cursor: "pointer",
                appearance: "auto",
              }}
            >
              {appLanguageOptions.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ==================== FORM ==================== */}
        <form onSubmit={handleVerifyApplication}>
          {/* SECTION: BASIC DETAILS */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaClipboardList />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.basicDetails || "Basic Details"}
            </h1>
          </div>

          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.date || "Date"}
                name="date"
                type="text"
                value={formData.date}
                disabled
                readOnly
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.studentNameTamil || "Student's Name (Tamil)"}
                name="applicant_name_in_tamil"
                type="text"
                value={formData.applicant_name_in_tamil}
                onChange={handleInputChange}
                onFocus={() => handleTamilInputFocus("applicant_name_in_tamil")}
                placeholder={
                  t?.studentNameTamilPlaceholder ||
                  "Enter Student Name in Tamil"
                }
                required
                error={errors.applicant_name_in_tamil}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.studentNameEnglish || "Student's Name (English)"}
                name="applicant_name"
                type="text"
                value={formData.applicant_name}
                onChange={handleInputChange}
                placeholder={
                  t?.studentNameEnglishPlaceholder ||
                  "Enter Student Name in English"
                }
                required
                error={errors.applicant_name}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={t?.dateOfBirth || "Date Of Birth"}
                name="date_of_birth"
                value={
                  formData.date_of_birth ? dayjs(formData.date_of_birth) : null
                }
                onChange={(date) => handleDateChange("date_of_birth", date)}
                placeholder={t?.dateOfBirthPlaceholder || "Ex: 30/07/2016"}
                required
                error={errors.date_of_birth}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.emailAddress || "Email"}
                name="email"
                type="text"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={t?.emailPlaceholder || "E.g. student@gmail.com"}
                required
                error={errors.email}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.mobileNumber || "Mobile No."}
                name="mobile_number"
                type="text"
                value={formData.mobile_number}
                onChange={handleInputChange}
                placeholder={
                  t?.mobileNumberPlaceholder ||
                  "Indian mobile: starts 6-9, 10 digits."
                }
                maxLength={10}
                required
                error={errors.mobile_number}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.aadharNumber || "Aadhaar No."}
                name="aadhar_no"
                type="text"
                value={formData.aadhar_no}
                onChange={handleInputChange}
                placeholder={
                  t?.aadharNumberAltPlaceholder || "Ex: 432156789076"
                }
                maxLength={12}
                required
                error={errors.aadhar_no}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.gender || "Gender"}
                name="gender"
                value={formData.gender}
                onChange={(value) => handleDropdownChange("gender", value)}
                options={genderOptions}
                placeholder={t?.selectGender || "Select Gender"}
                required
                error={errors.gender}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.religion || "Religion"}
                name="religion"
                value={formData.religion}
                onChange={(value) => handleDropdownChange("religion", value)}
                options={religionOptions}
                placeholder={t?.selectReligion || "Select Religion"}
                required
                error={errors.religion}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.caste || "Caste"}
                name="caste"
                type="text"
                value={formData.caste}
                onChange={handleInputChange}
                onFocus={() => handleTamilInputFocus("caste")}
                placeholder={t?.castePlaceholder || "E.g. Caste"}
                required
                error={errors.caste}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.category || "Category"}
                name="community"
                value={formData.community}
                onChange={(value) => handleDropdownChange("community", value)}
                options={categoryOptions}
                placeholder={t?.selectCategory || "Select Category"}
                required
                error={errors.community}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.nationality || "Nationality"}
                name="nationality"
                type="text"
                value={formData.nationality}
                onChange={handleInputChange}
                placeholder={t?.nationalityPlaceholder || "E.g. Indian"}
                required
                error={errors.nationality}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.state || "State"}
                name="student_native_state"
                value={formData.student_native_state}
                onChange={(value) =>
                  handleDropdownChange("student_native_state", value)
                }
                options={stateOptions}
                placeholder={t?.selectState || "Select State"}
                required
                error={errors.student_native_state}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.birthPlace || "Birth Place"}
                name="place_of_birth"
                type="text"
                value={formData.place_of_birth}
                onChange={handleInputChange}
                onFocus={() => handleTamilInputFocus("place_of_birth")}
                placeholder={t?.birthPlacePlaceholder || "E.g. Chennai"}
                required
                error={errors.place_of_birth}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomFileInput
                label={`${t?.markSheet} (${t?.copy})` || "Mark Sheet (Copy)"}
                name="community_certificate_path"
                value={formData.community_certificate_original_name || null}
                onChange={handleCommunityFileChange}
                accept=".pdf,.jpg,.jpeg,.png"
                placeholder={
                  formData.community_certificate_original_name ||
                  t?.noFileChosen ||
                  "No file chosen"
                }
                buttonText={t?.chooseFile || "Choose File"}
                required
                error={errors.community_certificate_path}
              />
              <small style={{ color: "#888", fontSize: "11px" }}>
                {t?.allowedPdfJpgPng || "Allowed: PDF/JPG/PNG."}
              </small>
            </div>
          </div>

          {/* SECTION: RESIDENTIAL DETAILS */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaClipboardList />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.residentialDetails || "Residential Details"}
            </h1>
          </div>

          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={
                  t?.areYouResidingWith ||
                  "Are you residing with parents or with a guardian?"
                }
                name="living_with"
                value={formData.living_with}
                onChange={(value) => handleDropdownChange("living_with", value)}
                options={livingWithOptions}
                placeholder={t?.select || "Select"}
                required
                error={errors.living_with}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.fullname || "Name"}
                name={getResidentialFieldName("parent_name", "guardian_name")}
                type="text"
                value={getResidentialValue("parent_name", "guardian_name")}
                onChange={handleInputChange}
                onFocus={() =>
                  handleTamilInputFocus(
                    getResidentialFieldName("parent_name", "guardian_name"),
                  )
                }
                placeholder={t?.enterHere || "Enter here.."}
                required
                error={getResidentialError("parent_name", "guardian_name")}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.occupation || "Occupation"}
                name={getResidentialFieldName(
                  "parent_occupation",
                  "guardian_occupation",
                )}
                value={getResidentialValue(
                  "parent_occupation",
                  "guardian_occupation",
                )}
                onChange={(value) =>
                  handleDropdownChange(
                    getResidentialFieldName(
                      "parent_occupation",
                      "guardian_occupation",
                    ),
                    value,
                  )
                }
                options={occupationOptions}
                placeholder={t?.selectOccupation || "Select Occupation"}
                required
                error={getResidentialError(
                  "parent_occupation",
                  "guardian_occupation",
                )}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.annualIncome || "Annual Income"}
                name={getResidentialFieldName(
                  "parent_annual_salary",
                  "guardian_annual_salary",
                )}
                type="text"
                value={getResidentialValue(
                  "parent_annual_salary",
                  "guardian_annual_salary",
                )}
                onChange={handleInputChange}
                placeholder="e.g., 250000"
                required
                error={getResidentialError(
                  "parent_annual_salary",
                  "guardian_annual_salary",
                )}
                maxLength={7}
              />
              <small style={{ color: "#888", fontSize: "11px" }}>
                {t?.onlyNumbersAllowed || "Only Numbers Allowed."}
              </small>
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.address || "Address"}
                name={getResidentialFieldName(
                  "parent_address",
                  "guardian_address",
                )}
                type="text"
                value={getResidentialValue(
                  "parent_address",
                  "guardian_address",
                )}
                onChange={handleInputChange}
                onFocus={() =>
                  handleTamilInputFocus(
                    getResidentialFieldName(
                      "parent_address",
                      "guardian_address",
                    ),
                  )
                }
                placeholder={t?.addressPlaceholder || "E.g. Address"}
                required
                error={getResidentialError(
                  "parent_address",
                  "guardian_address",
                )}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.district || "District"}
                name={getResidentialFieldName(
                  "parent_district",
                  "guardian_district",
                )}
                type="text"
                value={getResidentialValue(
                  "parent_district",
                  "guardian_district",
                )}
                onChange={handleInputChange}
                onFocus={() =>
                  handleTamilInputFocus(
                    getResidentialFieldName(
                      "parent_district",
                      "guardian_district",
                    ),
                  )
                }
                placeholder={t?.districtPlaceholder || "E.g. District"}
                required
                error={getResidentialError(
                  "parent_district",
                  "guardian_district",
                )}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.taluk || "Taluk"}
                name={getResidentialFieldName("parent_city", "guardian_city")}
                type="text"
                value={getResidentialValue("parent_city", "guardian_city")}
                onChange={handleInputChange}
                onFocus={() =>
                  handleTamilInputFocus(
                    getResidentialFieldName("parent_city", "guardian_city"),
                  )
                }
                placeholder={t?.talukPlaceholder || "E.g. Taluk"}
                required
                error={getResidentialError("parent_city", "guardian_city")}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.state || "State"}
                name={getResidentialFieldName("parent_state", "guardian_state")}
                value={getResidentialValue("parent_state", "guardian_state")}
                onChange={(value) =>
                  handleDropdownChange(
                    getResidentialFieldName("parent_state", "guardian_state"),
                    value,
                  )
                }
                options={stateOptions}
                placeholder={t?.selectState || "Select State"}
                required
                error={getResidentialError("parent_state", "guardian_state")}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.pincode || "Pincode"}
                name={getResidentialFieldName(
                  "parent_pincode",
                  "guardian_pincode",
                )}
                type="text"
                value={getResidentialValue(
                  "parent_pincode",
                  "guardian_pincode",
                )}
                onChange={handleInputChange}
                placeholder={t?.pincodePlaceholder || "6-digit pincode"}
                maxLength={6}
                required
                error={getResidentialError(
                  "parent_pincode",
                  "guardian_pincode",
                )}
              />
            </div>
          </div>

          {/* SECTION: ACADEMIC DETAILS */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaClipboardList />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.academicDetails || "Academic Details"}
            </h1>
          </div>

          <div className="row">
            <div className="col-lg-6 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={
                  t?.qualifyingExaminationPassed ||
                  "Qualifying Examination Passed"
                }
                name="qualified_eligibility_exam"
                value={formData.qualified_eligibility_exam}
                onChange={(value) =>
                  handleDropdownChange("qualified_eligibility_exam", value)
                }
                options={QUALIFYING_EXAM_OPTIONS}
                placeholder={t?.selectExamination || "Select Examination"}
                required
                error={errors.qualified_eligibility_exam}
              />
            </div>
            <div className="col-lg-6 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={
                  t?.nameOfExaminationBoard || "Name of the Examination Board"
                }
                name="exam_board_name"
                value={formData.exam_board_name}
                onChange={(value) =>
                  handleDropdownChange("exam_board_name", value)
                }
                options={EXAM_BOARD_OPTIONS}
                placeholder={t?.selectExamination || "Select Examination"}
                required
                error={errors.exam_board_name}
              />
            </div>
          </div>

          <div
            style={{
              padding: "10px 8px 16px",
              fontSize: "13px",
              color: "#555",
              fontStyle: "italic",
              lineHeight: 1.6,
            }}
          >
            {t?.publicMarksNote ||
              "The marks obtained in the qualifying examination must be duly attested either by the Headmaster of the school last studied or by a Gazetted Officer. The original mark certificate should NOT be sent along with the application. Only the attested copy of the mark statement should be enclosed with the application. The original mark statement must be produced at the time of admission."}
          </div>

          {/* SECTION: PUBLIC EXAMINATION MARKS */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaClipboardList />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.publicExaminationMarks || "Public Examination Marks"}
            </h1>
          </div>

          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={
                  t?.nameOfExaminationPassed || "Name of the Examination Passed"
                }
                name="qualified_exam_name"
                value={formData.qualified_exam_name}
                onChange={(value) =>
                  handleDropdownChange("qualified_exam_name", value)
                }
                options={QUALIFYING_EXAM_OPTIONS}
                placeholder={t?.selectExamination || "Select Examination"}
                required
                error={errors.qualified_exam_name}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={
                  t?.monthAndYearOfExamination ||
                  "Month and Year of Examination"
                }
                name="exam_month_and_year"
                value={
                  formData.exam_month_and_year
                    ? dayjs(formData.exam_month_and_year)
                    : null
                }
                onChange={(date) =>
                  handleMonthYearChange("exam_month_and_year", date)
                }
                placeholder="---------- ----"
                picker="month"
                required
                error={errors.exam_month_and_year}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={
                  t?.examinationRegisterNumber || "Examination Register Number"
                }
                name="register_number"
                type="text"
                value={formData.register_number}
                onChange={handleInputChange}
                placeholder={t?.enterHere || "Enter here.."}
                required
                error={errors.register_number}
                maxLength={10}
              />
            </div>
          </div>

          {/* Marks Table */}
          <div style={{ overflowX: "auto", padding: "0 8px 16px" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#e3f2fd" }}>
                  <th
                    style={{
                      padding: "10px 16px",
                      textAlign: "left",
                      fontWeight: 700,
                      border: "1px solid #c5d8f0",
                    }}
                  >
                    {t?.subject || "Subject"}
                  </th>
                  <th
                    style={{
                      padding: "10px 16px",
                      textAlign: "center",
                      fontWeight: 700,
                      border: "1px solid #c5d8f0",
                      width: "150px",
                    }}
                  >
                    {t?.maxMarks || "Max Marks"}
                  </th>
                  <th
                    style={{
                      padding: "10px 16px",
                      textAlign: "center",
                      fontWeight: 700,
                      border: "1px solid #c5d8f0",
                      width: "200px",
                    }}
                  >
                    {t?.marksObtained || "Marks Obtained"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: "TAMIL", field: "tamil_mark" },
                  { label: "ENGLISH", field: "english_mark" },
                  { label: "MATHEMATICS", field: "mathematics_mark" },
                  { label: "SCIENCE", field: "science_mark" },
                  { label: "SOCIAL SCIENCE", field: "social_science_mark" },
                ].map(({ label, field }) => (
                  <tr key={field} style={{ borderBottom: "1px solid #e0e0e0" }}>
                    <td
                      style={{
                        padding: "8px 16px",
                        border: "1px solid #e0e0e0",
                        fontWeight: 500,
                      }}
                    >
                      {label}
                    </td>
                    <td
                      style={{
                        padding: "8px 16px",
                        border: "1px solid #e0e0e0",
                        textAlign: "center",
                        color: "#555",
                      }}
                    >
                      100
                    </td>
                    <td
                      style={{
                        padding: "8px 8px",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <input
                        type="text"
                        name={field}
                        value={formData[field]}
                        onChange={handleInputChange}
                        placeholder="0-100"
                        maxLength={3}
                        style={{
                          width: "100%",
                          padding: "6px 10px",
                          border: errors[field]
                            ? "1px solid #d32f2f"
                            : "1px solid #ccc",
                          borderRadius: "4px",
                          fontSize: "14px",
                          textAlign: "center",
                          outline: "none",
                        }}
                      />
                      {errors[field] && (
                        <div
                          style={{
                            color: "#d32f2f",
                            fontSize: "11px",
                            marginTop: "2px",
                          }}
                        >
                          {errors[field]}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: "#f5f5f5", fontWeight: 700 }}>
                  <td
                    style={{
                      padding: "10px 16px",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    Overall
                  </td>
                  <td
                    style={{
                      padding: "10px 16px",
                      border: "1px solid #e0e0e0",
                      textAlign: "center",
                    }}
                  >
                    500
                  </td>
                  <td
                    style={{
                      padding: "10px 16px",
                      border: "1px solid #e0e0e0",
                      textAlign: "center",
                    }}
                  >
                    {formData.total_mark}
                  </td>
                </tr>
                <tr style={{ backgroundColor: "#f5f5f5", fontWeight: 700 }}>
                  <td
                    style={{
                      padding: "10px 16px",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    {t?.percentage || "Percentage"}
                  </td>
                  <td
                    style={{
                      padding: "10px 16px",
                      border: "1px solid #e0e0e0",
                    }}
                  />
                  <td
                    style={{
                      padding: "10px 16px",
                      border: "1px solid #e0e0e0",
                      textAlign: "center",
                    }}
                  >
                    {Number(formData.total_percentage).toFixed(2)} %
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* SECTION: PREFERRED COURSE DETAILS */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaClipboardList />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.preferredCourseDetails || "Preferred Course Details"}
            </h1>
          </div>

          <p
            style={{
              padding: "4px 8px 8px",
              fontSize: "13px",
              color: "#555",
              fontStyle: "italic",
            }}
          >
            {t?.preferredCourseNote ||
              "Please specify the order of preference for the stream you wish to study."}
          </p>

          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.preferredStudyCourse || "Course Preferred to Study"}
                name="preferred_study_course"
                value={formData.preferred_study_course}
                onChange={(value) =>
                  handleDropdownChange("preferred_study_course", value)
                }
                options={COURSE_PREFERRED_OPTIONS}
                placeholder={
                  t?.selectCoursePrefered || "Select Course Preferred"
                }
                required
                error={errors.preferred_study_course}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={
                  t?.yearOfAdmission ||
                  "Year of Admission (First Year / Second Year)"
                }
                name="admission_for_which_year"
                value={formData.admission_for_which_year}
                onChange={(value) =>
                  handleDropdownChange("admission_for_which_year", value)
                }
                options={YEAR_OF_ADMISSION_OPTIONS}
                placeholder={t?.select || "Select"}
                required
                error={errors.admission_for_which_year}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.preferredGroup1 || "Preferred Group 1"}
                name="preferred_group_1"
                value={formData.preferred_group_1}
                onChange={(value) =>
                  handleDropdownChange("preferred_group_1", value)
                }
                options={preferredGroupOptions}
                placeholder={
                  t?.selectPreferredGroup1 || "Select Preferred Group 1"
                }
                required
                error={errors.preferred_group_1}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.preferredGroup2 || "Preferred Group 2"}
                name="preferred_group_2"
                value={formData.preferred_group_2}
                onChange={(value) =>
                  handleDropdownChange("preferred_group_2", value)
                }
                options={preferredGroupOptions}
                placeholder={
                  t?.selectPreferredGroup2 || "Select Preferred Group 2"
                }
                error={errors.preferred_group_2}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.preferredGroup3 || "Preferred Group 3"}
                name="preferred_group_3"
                value={formData.preferred_group_3}
                onChange={(value) =>
                  handleDropdownChange("preferred_group_3", value)
                }
                options={preferredGroupOptions}
                placeholder={
                  t?.selectPreferredGroup3 || "Select Preferred Group 3"
                }
                error={errors.preferred_group_3}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.motherTongue || "Mother Tongue"}
                name="mother_tongue"
                value={formData.mother_tongue}
                onChange={(value) =>
                  handleDropdownChange("mother_tongue", value)
                }
                options={motherTongueOptions}
                placeholder={t?.selectMotherTongue || "Select Mother Tongue"}
                required
                error={errors.mother_tongue}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomRadioGroup
                label={
                  t?.mediumOfInstructionLeavingSchool ||
                  "Medium of Instruction at the Time of Leaving School"
                }
                name="last_medium_of_instruction"
                value={formData.last_medium_of_instruction}
                onChange={(value) =>
                  handleRadioChange("last_medium_of_instruction", value)
                }
                options={MEDIUM_OPTIONS}
                required
                error={errors.last_medium_of_instruction}
              />
            </div>
          </div>

          {/* SECTION: LAST STUDIED DETAILS */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaClipboardList />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.lastStudiedDetails || "Last Studied Details"}
            </h1>
          </div>

          <div className="row">
            <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.lastStudiedClass || "Last Studied Class"}
                name="last_studied_class"
                value={formData.last_studied_class}
                onChange={(value) =>
                  handleDropdownChange("last_studied_class", value)
                }
                options={classOptions}
                placeholder={t?.selectClass || "Select"}
                required
                error={errors.last_studied_class}
              />
            </div>
            <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={t?.studiedYear || "Year of Study"}
                name="studied_year"
                value={
                  formData.studied_year ? dayjs(formData.studied_year) : null
                }
                onChange={(date) => handleMonthYearChange("studied_year", date)}
                placeholder="---------- ----"
                picker="month"
                required
                error={errors.studied_year}
              />
            </div>
            <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={
                  t?.lastStudiedSchool || "Name of the Last Studied School"
                }
                name="last_studied_school_name"
                type="text"
                value={formData.last_studied_school_name}
                onChange={handleInputChange}
                onFocus={() =>
                  handleTamilInputFocus("last_studied_school_name")
                }
                placeholder={t?.enterHere || "Enter here.."}
                required
                error={errors.last_studied_school_name}
              />
            </div>
            <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.emisNo || "EMIS No."}
                name="emis_id"
                type="text"
                value={formData.emis_id}
                onChange={handleInputChange}
                placeholder="10-digit EMIS number"
                maxLength={10}
                required
                error={errors.emis_id}
              />
            </div>
            <div className="col-lg-12 px-2 col-md-12 col-sm-12">
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: 600,
                    fontSize: "13px",
                    marginBottom: "6px",
                    color: "#333",
                  }}
                >
                  {t?.reasonForLeaving ||
                    "Reason for Leaving or Discontinuation"}
                  <span style={{ color: "#d32f2f", marginLeft: "2px" }}>*</span>
                </label>
                <textarea
                  name="reason_for_discontinuing"
                  value={formData.reason_for_discontinuing}
                  onChange={handleInputChange}
                  onFocus={() =>
                    handleTamilInputFocus("reason_for_discontinuing")
                  }
                  placeholder={
                    t?.reasonForLeavingPlaceholder ||
                    "Enter reason for leaving or discontinuation"
                  }
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: errors.reason_for_discontinuing
                      ? "1px solid #d32f2f"
                      : "1px solid #ccc",
                    borderRadius: "6px",
                    fontSize: "14px",
                    resize: "vertical",
                    outline: "none",
                    fontFamily: "inherit",
                  }}
                />
                {errors.reason_for_discontinuing && (
                  <div
                    style={{
                      color: "#d32f2f",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.reason_for_discontinuing}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SECTION: OTHER DETAILS */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaClipboardList />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.otherDetails || "Other Details"}
            </h1>
          </div>

          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomRadioGroup
                label={
                  t?.nccNssScoutsGuides ||
                  "NCC / NSS / Scouts / Guides / Red Cross – Would you like to join?"
                }
                name="willing_to_join_services"
                value={formData.willing_to_join_services}
                onChange={(value) =>
                  handleRadioChange("willing_to_join_services", value)
                }
                options={YES_NO_OPTIONS}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomRadioGroup
                label={
                  t?.smallpoxVaccinationTaken || "Smallpox Vaccination Taken?"
                }
                name="is_vaccinated_pox"
                value={formData.is_vaccinated_pox}
                onChange={(value) =>
                  handleRadioChange("is_vaccinated_pox", value)
                }
                options={YES_NO_OPTIONS}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomFileInput
                label={
                  t?.certificatesForCoCurricular ||
                  "Certificates for Co-curricular Activities"
                }
                name="certificate_path"
                value={formData.certificate_original_name || null}
                onChange={handleCoCurricularFileChange}
                accept=".pdf,.jpg,.jpeg,.png"
                placeholder={
                  formData.certificate_original_name ||
                  t?.noFileChosen ||
                  "No file chosen"
                }
                buttonText={t?.chooseFile || "Choose File"}
                error={errors.certificate_path}
              />
              <small style={{ color: "#888", fontSize: "11px" }}>
                {t?.allowedPdfJpgPng || "Allowed: PDF/JPG/PNG."}
              </small>
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.place || "Place"}
                name="place"
                type="text"
                value={formData.place}
                onChange={handleInputChange}
                onFocus={() => handleTamilInputFocus("place")}
                placeholder={t?.enterHere || "Enter here.."}
                required
                error={errors.place}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomFileInput
                label={t?.studentImage || "Student Image"}
                name="student_image"
                value={formData.student_image_original_name || null}
                onChange={handleStudentImageChange}
                accept="image/*"
                placeholder={
                  formData.student_image_original_name ||
                  t?.noFileChosen ||
                  "No file chosen"
                }
                buttonText={t?.chooseFile || "Choose File"}
                required
                error={errors.student_image}
              />
              <small style={{ color: "#888", fontSize: "11px" }}>
                {t?.allowedJpgPng || "Allowed: JPG/PNG images."}
              </small>
            </div>
          </div>

          <div className={styles.buttonContainer}>
            <CustomButton
              type="submit"
              variant="contained"
              className={styles.addButton}
              fullWidth={false}
              loading={submitting}
              disabled={viewId && admissionStatus !== "Submitted"}
            >
              {isEditMode
                ? t?.verifyAndUpdate || "Verify & Update"
                : t?.verifyApplication || "Verify Application"}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default AdmissionApplicationFormXII;
